import {
  FailableAction,
  failableActionCreator,
  Loadable,
  PayloadAction,
  payloadActionCreator,
  Action,
  actionCreator,
} from '@library/react-toolkit'
import { LocationChangeAction } from 'connected-react-router'
import * as t from 'io-ts'
import { option } from 'fp-ts'
import { InvoiceWithTotal } from '../../types'

export type NotificationMethod = 'SMS' | 'EMAIL' | 'APP'

export type BillSort = [keyof InvoiceWithTotal, 'asc' | 'desc']

export type Query = { page: number; itemsPerPage: number; sort?: BillSort }

export const FinancingRequest = t.type({
  status: t.keyof({
    'Posted Request': null,
    'Pending Offers': null,
    Financed: null,
  }),
})
export const OrganizationInfo = t.type({
  id: t.number,
  displayName: t.string,
  crNumber: t.string,
  activity: t.string,
  asset: t.string,
  country: t.string,
  createdBy: t.string,
  logoUrl: t.string,
})
export type OrganizationInfo = t.TypeOf<typeof OrganizationInfo>

export const PublishedInvoice = t.type({
  id: t.number,
  payer: t.string,
  amount: t.number,
  createdAt: t.string,
  createdBy: t.string,
  status: t.keyof({
    Published: {},
    Financed: {},
  }),
  updatedAt: t.string,
})

export type PublishedInvoice = t.TypeOf<typeof PublishedInvoice>

export type State = {
  sendResult: Loadable<unknown>
  importResult: Loadable<unknown>
  addResult: Loadable<undefined>
  viewBill: option.Option<InvoiceWithTotal>
  editBill: option.Option<InvoiceWithTotal>
  mode: 'organization' | 'supplier'
  selectedBills: {
    organization: Record<string, InvoiceWithTotal>
    supplier: Record<string, InvoiceWithTotal>
  }
  addModalVisible: boolean
}

export const emptyBill: Readonly<InvoiceWithTotal> = {
  notifyPhonenumber: undefined,
  supplier: undefined,
  organization: '',
  id: 0,
  reference: '',
  unitID: '',
  amount: 0,
  discount: undefined,
  tax: undefined,
  customer: {
    id: 0,
    name: '',
    email: '',
    address: '',
    taxNumber: '',
    phoneNumber: '',
    nymId: undefined,
  },
  payer: undefined,
  status: 'UNPAID',
  dueAt: new Date('2020-06-12T12:03:22.836Z'),
  createdAt: new Date('2020-06-12T12:03:22.836Z'),
  items: [],
  urlOk: undefined,
  urlFailed: undefined,
  total: 0,
}

export type SendBillsAction = PayloadAction<
  'BILL/send_bill',
  { method: NotificationMethod; ids: number[] }
>
export const sendBillsAction =
  payloadActionCreator<SendBillsAction>('BILL/send_bill')

export type SendBillResultAction = FailableAction<
  'BILL/send_bill_result',
  unknown
>
export const sendBillResultAction = failableActionCreator<SendBillResultAction>(
  'BILL/send_bill_result'
)

export type ViewBillAction = PayloadAction<'BILL/view_bill', InvoiceWithTotal>
export const viewBillAction =
  payloadActionCreator<ViewBillAction>('BILL/view_bill')

export type CloseViewAction = Action<'BILL/close_view_bill'>
export const closeViewAction = actionCreator<CloseViewAction>(
  'BILL/close_view_bill'
)

export type EditBillAction = PayloadAction<'BILL/edit_bill', InvoiceWithTotal>
export const editBillAction =
  payloadActionCreator<EditBillAction>('BILL/edit_bill')

export type CloseEditAction = Action<'BILL/close_edit_bill'>
export const closeEditAction = actionCreator<CloseEditAction>(
  'BILL/close_edit_bill'
)

export type SetModeAction = PayloadAction<'BILL/set_mode', State['mode']>
export const setModeAction =
  payloadActionCreator<SetModeAction>('BILL/set_mode')

export type SetSelectedBillsAction = PayloadAction<
  'BILL/set_selected_bills',
  Record<string, InvoiceWithTotal>
>
export const setSelectedBillsAction =
  payloadActionCreator<SetSelectedBillsAction>('BILL/set_selected_bills')

export type SetAddModalVisible = PayloadAction<
  'BILL/set_add_modale_visible',
  boolean
>
export const setAddModalVisible = payloadActionCreator<SetAddModalVisible>(
  'BILL/set_add_modale_visible'
)

export type Actions =
  | LocationChangeAction
  | SendBillsAction
  | SendBillResultAction
  | ViewBillAction
  | CloseViewAction
  | EditBillAction
  | CloseEditAction
  | SetModeAction
  | SetSelectedBillsAction
  | SetAddModalVisible
